// Customer-side plan-change request hooks.
//
// Endpoint contract (backend: services/api/src/routes/plan-change-requests.ts):
//   GET  /api/v1/plan-change-requests/me        -> pending request for caller's org (or null)
//   POST /api/v1/plan-change-requests           -> submit a new request
//   POST /api/v1/plan-change-requests/me/cancel -> cancel caller's pending request

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchRaw, post } from "@/lib/api";
import { API_BASE } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { keys } from "./keys";

export type PlanTier = "free" | "growth" | "scale" | "enterprise";
export type PlanChangeStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface PlanChangeRequest {
  id: string;
  orgId: string;
  requestedPlan: PlanTier;
  status: PlanChangeStatus;
  requestedByUser: string;
  reviewedByUser: string | null;
  reviewerNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
  // Joined org info (admin list only — undefined on /me responses)
  orgName?: string;
  orgSlug?: string;
  currentPlan?: PlanTier;
}

/** Pending request for the caller's org. Null when no pending request exists.
 *
 * We use apiFetchRaw + manual unwrap because apiFetch's `.data ?? json` falls
 * through when data is explicitly null — it returns the wrapper `{ data: null }`
 * instead of null, which would render an empty-state banner with a missing
 * requestedPlan. */
export function useMyPendingPlanChangeRequest() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: keys.planChangeRequests.mine(orgId),
    queryFn: async () => {
      const body = await apiFetchRaw<{ data: PlanChangeRequest | null }>(
        `${API_BASE}/api/v1/plan-change-requests/me`,
      );
      return body.data;
    },
    enabled: !!orgId,
    staleTime: 10_000,
  });
}

/** Submit a new plan change request. */
export function useCreatePlanChangeRequest() {
  const qc = useQueryClient();
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (requestedPlan: PlanTier) =>
      apiFetch<PlanChangeRequest>(
        `${API_BASE}/api/v1/plan-change-requests`,
        post({ requestedPlan }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.planChangeRequests.mine(orgId) });
    },
  });
}

/** Cancel the caller's pending request. */
export function useCancelPlanChangeRequest() {
  const qc = useQueryClient();
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: () =>
      apiFetch<PlanChangeRequest>(
        `${API_BASE}/api/v1/plan-change-requests/me/cancel`,
        post({}),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.planChangeRequests.mine(orgId) });
    },
  });
}

// ─── Platform admin hooks ────────────────────────────────────────────────────

/** List of pending plan-change requests across all orgs (platform_admin only). */
export function usePendingPlanChangeRequests() {
  return useQuery({
    queryKey: keys.planChangeRequests.pending(),
    queryFn: async () => {
      const body = await apiFetchRaw<{ data: PlanChangeRequest[] }>(
        `${API_BASE}/api/v1/plan-change-requests?status=pending`,
      );
      return body.data;
    },
    staleTime: 10_000,
  });
}

interface ReviewVars {
  id: string;
  reviewerNote?: string;
}

/** Approve a pending request — sets orgs.plan to requested_plan atomically. */
export function useApprovePlanChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewerNote }: ReviewVars) =>
      apiFetch<PlanChangeRequest>(
        `${API_BASE}/api/v1/plan-change-requests/${id}/approve`,
        post(reviewerNote ? { reviewerNote } : {}),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.planChangeRequests.pending() });
      // The plan IS the credit limit: approving rewrites orgs.plan, which is
      // what /usage/ai reads to compute limitCents. Without this the topbar
      // badge keeps quoting the OLD plan's allowance - reported as "31 of 50
      // credits left" persisting for ~20 minutes and only correcting after a
      // hard reload. Polling alone is not a guarantee: React Query pauses its
      // interval while the tab is hidden, so a backgrounded session can hold a
      // stale allowance indefinitely.
      qc.invalidateQueries({ queryKey: keys.aiUsage.any() });
    },
  });
}

/** Reject a pending request — leaves orgs.plan unchanged. */
export function useRejectPlanChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewerNote }: ReviewVars) =>
      apiFetch<PlanChangeRequest>(
        `${API_BASE}/api/v1/plan-change-requests/${id}/reject`,
        post(reviewerNote ? { reviewerNote } : {}),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.planChangeRequests.pending() });
    },
  });
}
